import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Radio, Plus, Search, Copy, Trash2, MoreVertical, Signal, CircleDot,
  MapPin, Loader2, Power, Video,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HardwareCheck } from "@/components/HardwareCheck";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const LOCATIONS = ["US-East (Virginia)", "US-West (Oregon)", "EU-West (Frankfurt)", "Asia (Singapore)"];

function StatusBadge({ status }) {
  const active = status === "active";
  return (
    <span
      data-testid="stream-status-badge"
      className={
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border " +
        (active
          ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
          : "bg-gray-500/15 text-gray-400 border-gray-500/30")
      }
    >
      <span className={"h-1.5 w-1.5 rounded-full " + (active ? "bg-emerald-400 animate-live-dot" : "bg-gray-500")} />
      {active ? "Active" : "Offline"}
    </span>
  );
}

export default function Streams() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", server_location: LOCATIONS[0] });

  const load = async () => {
    try {
      const res = await api.get("/streams");
      setStreams(res.data);
    } catch (e) {
      toast.error("Failed to load streams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user?.role]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Stream name is required"); return; }
    setCreating(true);
    try {
      await api.post("/streams", form);
      toast.success("Stream created");
      setCreateOpen(false);
      setForm({ title: "", description: "", server_location: LOCATIONS[0] });
      load();
    } catch (e) {
      toast.error("Failed to create stream");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/streams/${deleteTarget.id}`);
      toast.success("Stream deleted");
      setStreams((s) => s.filter((x) => x.id !== deleteTarget.id));
    } catch (e) {
      toast.error("Failed to delete");
    } finally {
      setDeleteTarget(null);
    }
  };

  const toggleStatus = async (stream) => {
    try {
      const res = await api.post(`/streams/${stream.id}/toggle-status`);
      setStreams((s) => s.map((x) => (x.id === stream.id ? res.data : x)));
      toast.success(res.data.status === "active" ? "Stream is now live" : "Stream set offline");
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const filtered = streams.filter((s) => {
    const q = query.toLowerCase();
    const matchQ = s.title.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
    const matchF = filter === "all" || s.status === filter;
    return matchQ && matchF;
  });

  const activeCount = streams.filter((s) => s.status === "active").length;

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">
            Streams
            {user?.role === "admin" && (
              <span className="text-xs font-semibold px-2 py-1 rounded-md bg-amber-500/15 text-amber-400 border border-amber-500/30">ADMIN · ALL TENANTS</span>
            )}
          </h1>
          <p className="text-gray-400 mt-1.5 text-sm">
            {streams.length} stream{streams.length !== 1 && "s"} · <span className="text-emerald-400">{activeCount} live</span>
          </p>
        </div>
        <div className="flex items-center gap-3 w-max">
          <HardwareCheck />
          <button
            data-testid="create-stream-button"
            onClick={() => setCreateOpen(true)}
            className="gold-gradient hover:brightness-110 text-black font-semibold px-4 py-2.5 rounded-xl flex items-center gap-2 shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98] w-max cursor-pointer"
          >
            <Plus className="h-4 w-4" /> Create New Stream
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            data-testid="stream-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search streams…"
            className="pl-10 bg-card border-gray-800 focus-visible:ring-amber-500/40 h-11"
          />
        </div>
        <div className="flex gap-2">
          {["all", "active", "offline"].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={
                "px-4 h-11 rounded-lg text-sm font-medium capitalize transition-colors border " +
                (filter === f
                  ? "bg-amber-500/15 text-amber-400 border-amber-500/40"
                  : "bg-card text-gray-400 border-gray-800 hover:text-white")
              }
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-6 w-6 text-amber-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-gray-800 rounded-2xl">
          <div className="h-14 w-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto">
            <Radio className="h-7 w-7 text-amber-400" />
          </div>
          <p className="mt-4 text-white font-medium">No streams found</p>
          <p className="text-gray-500 text-sm mt-1">Create your first RTMP stream to get started.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((stream, i) => (
            <motion.div
              key={stream.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.05, 0.4) }}
              data-testid={`stream-card-${stream.id}`}
              onClick={() => navigate(`/streams/${stream.id}`)}
              className="group bg-card border border-gray-800 hover:border-amber-500/40 rounded-2xl p-5 transition-all cursor-pointer relative overflow-hidden"
            >
              <div className="absolute -right-16 -top-16 h-32 w-32 rounded-full bg-amber-500/5 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="flex items-start justify-between relative">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={"h-11 w-11 rounded-xl flex items-center justify-center shrink-0 " + (stream.status === "active" ? "bg-emerald-500/15" : "bg-gray-800")}>
                    <Video className={"h-5 w-5 " + (stream.status === "active" ? "text-emerald-400" : "text-gray-500")} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-white truncate">{stream.title}</p>
                    <p className="text-xs text-gray-500 truncate">{stream.description || "No description"}</p>
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`stream-menu-${stream.id}`} className="h-8 w-8 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 flex items-center justify-center transition-colors">
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-card border-gray-800 text-gray-200">
                      <DropdownMenuItem onClick={() => navigate(`/streams/${stream.id}`)} className="cursor-pointer focus:bg-gray-800">View details</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleStatus(stream)} className="cursor-pointer focus:bg-gray-800">
                        <Power className="h-4 w-4 mr-2" /> {stream.status === "active" ? "Set offline" : "Go live"}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => copy(stream.rtmp_url, "RTMP URL")} className="cursor-pointer focus:bg-gray-800">Copy RTMP URL</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => copy(stream.stream_key, "Stream Key")} className="cursor-pointer focus:bg-gray-800">Copy Stream Key</DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-gray-800" />
                      <DropdownMenuItem onClick={() => setDeleteTarget(stream)} className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-400">
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4 relative">
                <StatusBadge status={stream.status} />
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  <Signal className="h-3 w-3" /> LIVE PUSH
                </span>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-800/70 flex items-center justify-between relative">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 min-w-0">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{stream.server_location}</span>
                </div>
                {user?.role === "admin" && (
                  <span className="text-[10px] text-gray-600 truncate max-w-[110px]">{stream.owner_name}</span>
                )}
              </div>

              <div className="mt-3 flex items-center gap-2 relative" onClick={(e) => e.stopPropagation()}>
                <div className="flex-1 flex items-center gap-2 bg-gray-950/80 rounded-lg px-3 py-2 border border-gray-800 min-w-0">
                  <CircleDot className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                  <code className="text-[11px] text-gray-400 truncate font-mono">{stream.rtmp_url}</code>
                </div>
                <button
                  onClick={() => copy(stream.rtmp_url, "RTMP URL")}
                  className="h-9 w-9 rounded-lg bg-gray-950/80 border border-gray-800 text-gray-400 hover:text-amber-400 hover:border-amber-500/40 flex items-center justify-center transition-colors shrink-0"
                >
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-card border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Create New Stream</DialogTitle>
            <DialogDescription className="text-gray-400">A unique RTMP URL and stream key will be generated automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-gray-300">Stream Name</Label>
              <Input data-testid="new-stream-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Sunday Service Live" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Description</Label>
              <Textarea data-testid="new-stream-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 resize-none" rows={2} />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Server Location</Label>
              <Select value={form.server_location} onValueChange={(v) => setForm({ ...form, server_location: v })}>
                <SelectTrigger data-testid="new-stream-location" className="bg-gray-950 border-gray-800 focus:ring-amber-500/40"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-gray-800 text-white">
                  {LOCATIONS.map((l) => <SelectItem key={l} value={l} className="focus:bg-gray-800">{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setCreateOpen(false)} className="px-4 py-2 rounded-lg text-gray-400 hover:text-white text-sm">Cancel</button>
            <button data-testid="submit-create-stream" onClick={handleCreate} disabled={creating} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60 cursor-pointer">
              {creating && <Loader2 className="h-4 w-4 animate-spin" />} Create Stream
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-gray-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this stream?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              "{deleteTarget?.title}" will be permanently removed along with its RTMP endpoint. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-stream" onClick={handleDelete} className="bg-red-600 hover:bg-red-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
